import type { QueryClient, UseQueryResult } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getQueryClient } from 'app/get-query-client';
import { createApiClient } from 'lib/api/createApiClient';
import type { StructuredTargetVo } from 'models/milestones';
import type { GetStructuredTargetResponse, GetStructuredTargetsResponse } from 'models/reqres';
import config from 'utils/config';

const api = createApiClient({
    baseURL: `${config.exchangeApiUrl}/v1`,
});

const STRUCTURED_TARGETS_BATCH_SIZE = 150;
const STRUCTURED_TARGETS_PAGE_SIZE = 1000;

export const structuredTargetKeys = {
    all: ['structuredTargets', 'all'] as const,
    /** Single normalized `id -> StructuredTargetVo` map for O(1) imperative reads. */
    normalized: ['structuredTargets', 'normalized'] as const,
    byId: (id: string) => ['structuredTargets', 'byId', id] as const,
    byIds: (ids: readonly string[]) => ['structuredTargets', 'byIds', ids] as const,
};

function normalizeStructuredTargetIds(ids: readonly string[]): string[] {
    return Array.from(new Set(ids.filter(Boolean))).sort();
}

function chunkStructuredTargetIds(ids: readonly string[]): string[][] {
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += STRUCTURED_TARGETS_BATCH_SIZE) {
        chunks.push(ids.slice(i, i + STRUCTURED_TARGETS_BATCH_SIZE));
    }
    return chunks;
}

/** Normalized structured-target map stored in the React Query cache. */
export type StructuredTargetMap = Record<string, StructuredTargetVo>;

/**
 * Merges fetched targets into the single `structuredTargetKeys.normalized` map
 * so non-React consumers (plain transform functions, e.g. `marketEntityTransform.ts`) can
 * resolve a target by id via `getCachedStructuredTargetById` without knowing which `byIds`
 * query holds it.
 */
function mergeIntoNormalizedTargets(queryClient: QueryClient, targets: StructuredTargetVo[]): void {
    if (!targets.length) return;
    queryClient.setQueryData<StructuredTargetMap>(structuredTargetKeys.normalized, (prev) => {
        const next = { ...(prev ?? {}) };
        for (const target of targets) next[target.id] = target;
        return next;
    });
}

/**
 * Seeds structured targets into the normalized cache from an SSR/BFF payload so
 * imperative `getCachedStructuredTargetById` reads resolve immediately.
 */
export function seedStructuredTargetsIntoCache(queryClient: QueryClient, targets: StructuredTargetVo[]): void {
    mergeIntoNormalizedTargets(queryClient, targets);
}

/**
 * Imperative O(1) lookup of a structured target from the normalized cache.
 *
 * Pass the request-scoped `queryClient` (the one seeded during SSR) when reading
 * from a server-render path: `getQueryClient()` makes a fresh client on every
 * server call, so the default would never see the SSR-seeded targets and would
 * render custom-strike markets without abbreviations/team palettes until the
 * browser singleton is seeded. On the client the default browser singleton is
 * correct, so callers without a scoped client can omit it.
 */
export function getCachedStructuredTargetById(
    id: string | undefined | null,
    queryClient: QueryClient = getQueryClient(),
): StructuredTargetVo | undefined {
    if (!id) return undefined;
    const map = queryClient.getQueryData<StructuredTargetMap>(structuredTargetKeys.normalized);
    return map?.[id];
}

/** Fetches structured targets by id, batching requests, and returns them keyed by id (no cache write). */
async function fetchStructuredTargetsByIds(
    ids: readonly string[],
    signal?: AbortSignal,
): Promise<Record<string, StructuredTargetVo>> {
    const batches = chunkStructuredTargetIds(ids);
    const responses = await Promise.all(
        batches.map((batch) => {
            const params = new URLSearchParams({ page_size: '1000' });
            for (const id of batch) {
                params.append('ids', id);
            }

            // Trailing slash matters: `/structured_targets?ids=…` 301s, which axios won't replay the
            // query string for on cross-origin redirects. `/structured_targets/?ids=…` returns 200.
            return api.get<GetStructuredTargetsResponse>(`/structured_targets/?${params.toString()}`, {
                signal,
            });
        }),
    );

    const targetsById: Record<string, StructuredTargetVo> = {};
    for (const response of responses) {
        for (const target of response.data.structured_targets ?? []) {
            targetsById[target.id] = target;
        }
    }
    return targetsById;
}

/**
 * Fetch structured targets by id and map them for O(1) lookup. Targets are
 * effectively immutable once seeded (team/player/album metadata), so keep
 * them warm for the full session.
 */
export function useStructuredTargetsQuery(ids: readonly string[]): {
    data: Record<string, StructuredTargetVo> | undefined;
    isLoading: boolean;
    isFetching: boolean;
} {
    const queryClient = useQueryClient();
    // Sort to keep the queryKey identity stable regardless of input order.
    const sortedIds = normalizeStructuredTargetIds(ids);

    // Assemble the requested ids from the SSR/BFF-seeded normalized cache so this
    // `byIds` query has data on first paint instead of returning `undefined` until
    // a client request lands (the old `MilestonesStore.hydrate` path had the data
    // immediately). When every id is already seeded we use it as `initialData`
    // (targets are immutable, so no refetch); a partial hit becomes `placeholderData`
    // so the seeded targets paint while a background fetch fills the gaps.
    const readSeeded = (): Record<string, StructuredTargetVo> | undefined => {
        const normalized = queryClient.getQueryData<StructuredTargetMap>(structuredTargetKeys.normalized);
        if (!normalized) return undefined;
        const seeded: Record<string, StructuredTargetVo> = {};
        for (const id of sortedIds) if (normalized[id]) seeded[id] = normalized[id];
        return Object.keys(seeded).length > 0 ? seeded : undefined;
    };

    const query = useQuery({
        queryKey: structuredTargetKeys.byIds(sortedIds),
        queryFn: async ({ signal }) => {
            const targetsById = await fetchStructuredTargetsByIds(sortedIds, signal);
            mergeIntoNormalizedTargets(queryClient, Object.values(targetsById));
            return targetsById;
        },
        enabled: sortedIds.length > 0,
        staleTime: 60 * 60 * 1000,
        initialData: () => {
            const seeded = readSeeded();
            return seeded && Object.keys(seeded).length === sortedIds.length ? seeded : undefined;
        },
        // Keep already-fetched targets rendered while pagination grows the id
        // set; consumers treat only their own missing ids as pending. Fall back
        // to the seeded cache when there's no previous query result yet.
        placeholderData: (prev) => prev ?? readSeeded(),
    });

    // `placeholderData` makes TanStack's own `isLoading` report `false` as soon as a
    // partial (seeded) result exists, even while the rest of `sortedIds` are still
    // in flight — recompute it ourselves from whether every requested id has landed.
    const isComplete = sortedIds.every((id) => query.data?.[id] !== undefined);
    return {
        data: query.data,
        isLoading: query.isFetching && !isComplete,
        isFetching: query.isFetching,
    };
}

/**
 * Imperatively warms the structured-targets cache by id and merges into the
 * normalized map. For non-React preloads (e.g. client-side event switches) —
 * mirrors `MilestonesStore.loadCustomStrikeStructuredTargets`.
 */
export async function prefetchStructuredTargetsByIds(queryClient: QueryClient, ids: readonly string[]): Promise<void> {
    const sortedIds = normalizeStructuredTargetIds(ids);
    if (!sortedIds.length) return;
    await queryClient.prefetchQuery({
        queryKey: structuredTargetKeys.byIds(sortedIds),
        queryFn: async ({ signal }) => {
            const targetsById = await fetchStructuredTargetsByIds(sortedIds, signal);
            mergeIntoNormalizedTargets(queryClient, Object.values(targetsById));
            return targetsById;
        },
        staleTime: 60 * 60 * 1000,
    });
}

/** Fetch a single structured target by id. Mirrors `MilestonesStore.loadStructuredTarget`. */
export async function fetchStructuredTarget(id: string, signal?: AbortSignal): Promise<StructuredTargetVo> {
    const response = await api.get<GetStructuredTargetResponse>(`/structured_targets/${id}`, { signal });
    return response.data.structured_target;
}

export function useStructuredTargetQuery(id: string | null | undefined): UseQueryResult<StructuredTargetVo, Error> {
    const queryClient = useQueryClient();
    return useQuery({
        queryKey: structuredTargetKeys.byId(id ?? ''),
        queryFn: async ({ signal }) => {
            const target = await fetchStructuredTarget(id as string, signal);
            mergeIntoNormalizedTargets(queryClient, [target]);
            return target;
        },
        enabled: !!id,
        staleTime: 60 * 60 * 1000,
    });
}

/**
 * Fetches the entire structured-targets catalog, following pagination cursors
 * internally. Mirrors `MilestonesStore.loadStructuredTargets`. Single call site
 * today (`SuperbowlHero`) — everything else fetches by id via
 * `useStructuredTargetsQuery`.
 */
export async function fetchAllStructuredTargets(signal?: AbortSignal): Promise<StructuredTargetVo[]> {
    const structuredTargets: StructuredTargetVo[] = [];
    let cursor: string | undefined;

    do {
        const params = new URLSearchParams({ page_size: String(STRUCTURED_TARGETS_PAGE_SIZE) });
        if (cursor) params.set('cursor', cursor);
        const response = await api.get<GetStructuredTargetsResponse>(`/structured_targets/?${params.toString()}`, {
            signal,
        });
        structuredTargets.push(...response.data.structured_targets);
        cursor = response.data.cursor;
    } while (cursor);

    return structuredTargets;
}

export function useAllStructuredTargetsQuery(enabled = true): UseQueryResult<StructuredTargetVo[], Error> {
    const queryClient = useQueryClient();
    return useQuery({
        queryKey: structuredTargetKeys.all,
        queryFn: async ({ signal }) => {
            const targets = await fetchAllStructuredTargets(signal);
            // Also merge into the normalized map so imperative `getCachedStructuredTargetById`
            // reads (e.g. BinaryMarket color/abbreviation getters) resolve, as the by-id paths do.
            mergeIntoNormalizedTargets(queryClient, targets);
            return targets;
        },
        enabled,
        staleTime: 60 * 60 * 1000,
    });
}
